import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useRouter } from "@tanstack/react-router";
import loginPlaceholder from "../../assets/images/login-placeholder.png";
import { Eye, EyeOff } from "lucide-react";
import dandarLogo from "../../assets/images/dandar-logo.png";
import { UserRole } from "../../types/auth";
import { AuthService } from "../../services/api";
import { jwtDecode } from "jwt-decode";
import ForgotPassword from "./ForgotPassword";
import {BuggyComponent}  from '../../Bug'
interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    token: string;
  };
}

interface DecodedToken {
  id: string;
  username: string;
  role: string;
  iat: number;
  exp: number;
}

console.log("LoginPage");

const DEFAULT_CREDENTIALS = {
  admin: {
    username: "Rajneesh12",
    password: "Rajneesh@123",
  },
  clinicAdmin: {
    username: "",
    password: "",
  },
   labAdmin: {
    username: "Lab123",
    password: "Lab@1234",
  },
};

const DEFAULT_AVATARS = {
  admin: "https://ui-avatars.com/api/?name=Admin+User&background=6366f1&color=fff",
  clinicAdmin: "https://ui-avatars.com/api/?name=Clinic+Admin&background=10b981&color=fff",
};

export function LoginPage() {
  const router = useRouter();
  const { login } = useAuth();
  const role = router.state.location.pathname.includes("admin") ? "admin" :  router.state.location.pathname.includes("lab") ? "labAdmin" :  "clinicAdmin";

  const [formData, setFormData] = useState<LoginCredentials>(DEFAULT_CREDENTIALS[role]);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const defaultRole = router.state.location.pathname.includes("admin")
        ? "Admin"
        : router.state.location.pathname.includes("clinic")
        ? "Clinic"
        : router.state.location.pathname.includes("lab")
        ? "Lab"
        : "MedicalCamp";

      const response: LoginResponse = await AuthService.login({
        username: formData.username,
        password: formData.password,
        role: defaultRole,
      });

      if (!response.success || !response.data?.token) {
        throw new Error(response.message || "Login failed");
      }

      const token = response.data.token;
      let decodedToken: DecodedToken;

      try {
        decodedToken = jwtDecode<DecodedToken>(token);
        console.log("Decoded token:", decodedToken);

        AuthService.setToken(token);

        const userData = {
          id: decodedToken.id,
          username: decodedToken.username,
          role: decodedToken.role.toLowerCase() as UserRole,
          avatar: DEFAULT_AVATARS[decodedToken.role.toLowerCase() === "admin" ? "admin" : "clinicAdmin"],
          token,
          tokenData: decodedToken,
        };

        login(userData);

        // Navigate based on the role from token
        const basePath = decodedToken.role.toLowerCase() === "admin" ? "/admin" : "/clinic";
        router.navigate({ to: `${basePath}/dashboard` });
      } catch (decodeError: any) {
        console.error("Error decoding token:", decodeError);
        throw new Error(decodeError.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen flex">
      {isForgotPassword ? (
        <ForgotPassword onBack={() => setIsForgotPassword(false)} />
      ) : (
        <>
          {/* Left Section - Login Form */}
          <div className="w-2/5 flex items-center justify-center p-20">
            <div className="w-full max-w-md">
              {/* Logo */}
              <img src={dandarLogo} alt="Dandar Health" className="h-20 w-60 mb-8" />

              {/* Login Form */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Login</h1>
                  <p className="text-sm text-gray-600 mt-1">Enter your username and password to login</p>
                </div>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Enter username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-emerald-600 text-white py-2 px-4 rounded-md text-sm font-medium 
                hover:bg-emerald-700 transition-colors duration-200
                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-0
                disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading ? "Logging in..." : "LOGIN"}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Right Section - Image */}
          <div className="w-3/5 relative h-screen">
            <img src={loginPlaceholder} alt="Doctor with stethoscope" className="w-full h-full object-cover object-center" />
          </div>
        </>
      )}


    </div>
  );
}
